import { EncChannel, EncField, Encoding, EncodingResult } from '@mode-vegas/vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';

import { SelectedChartService } from '@mode/charts/contract';
import { EncodingHelpers, TableEncodingHelpers } from '@mode/charts/util-vegas';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { CompiledAttr, CompiledField, CompiledFormula } from '@mode/shared/util-switch';
import { ToastService } from '@mode/shared/util-toast';
import { BehaviorSubject, interval, Observable, Subject } from 'rxjs';
import { takeUntil, throttle } from 'rxjs/operators';
import {
  addOrReplaceField,
  addTrendToEncoding,
  updateOrMoveField,
  ZoneType,
} from '../../../../lib/chart-encoding-helpers';

/**
 * Helper functions for the encoding builders to consolidate common logic
 */

export class DropZone {
  dragCounter: number;
  chartType: ChartTypes.ChartType;
  dropAreaOffset: DOMRect; // Offset details of drop area where Pill is dropped
  dragEventStream = new Subject<DragEvent>(); // Event stream to handle Drag-Over events
  dragEventStream$: Observable<DragEvent>;
  dropIndex: number; // index of place where pill is currently hovered
  dropIndexStream = new BehaviorSubject<number>(-1); // Event stream to handle Drag-Index changes

  public hoverZone: EncChannel | string; // either channel or a pill
  public dropIndexStream$: Observable<number>;

  // TODO: Maybe this should become an options hash
  constructor(
    private name: string,
    private channel: EncChannel,
    protected chartService: SelectedChartService,
    private alertService: ToastService,
    private analyticsService: AnalyticsService,
    private chartType$: Observable<string>,
    private destroyed$: Subject<void>,
    private getExistingField: (view: FlamingoTypes.ModeView, encoding: Encoding, field: CompiledFormula) => EncField,
    private canDropTest: (channel: EncChannel | string, field: CompiledFormula) => boolean | undefined,
    private onRemoveField: (field: EncField) => void,
    private dropAreaSelector: string = null
  ) {
    this.dragCounter = 0;
    this.chartType$.pipe(takeUntil(this.destroyed$)).subscribe((type: ChartTypes.ChartType) => {
      this.chartType = type;
    });
    this.dropIndex = -1;

    if (this.dropAreaSelector) {
      this.dragEventStream$ = this.dragEventStream.asObservable();
      this.dropIndexStream$ = this.dropIndexStream.asObservable();
      this.subscribeDropIndexHandler();
    }
  }

  addTableField(encoding: Encoding, field: CompiledAttr, fieldId?: string) {
    if (TableEncodingHelpers.hasField(encoding, field.formula)) {
      this.alertService.error(`
              ${this.name} already includes ${field.fieldName}.`);
      this.chartService.endDrag();
      return;
    }
    return TableEncodingHelpers.addFieldToTableEncoding(encoding, field, fieldId);
  }

  reorderField(encoding: Encoding, field: CompiledField, fieldId?: string) {
    // Reorder, use relative direction of move to determine whether the field
    // should go above or below the target
    let fieldArray = encoding.value;
    let adjustedDropIndex: number;

    switch (this.channel) {
      case 'text':
      case 'detail':
        fieldArray = encoding.marks.properties[this.channel].values;
        break;
    }

    // TODO: Combo needs a different setup
    const sourceIndex = fieldArray.findIndex((f) => f.id === field.alias);

    // if target is present then it means dropAreaSelector is not present hence dropIndex cannot be used
    if (fieldId) {
      adjustedDropIndex = fieldArray.findIndex((f) => f.id === fieldId);
    } else {
      // as dropIndex cannot be greater than number of fields
      adjustedDropIndex = this.dropIndex > fieldArray.length ? fieldArray.length : this.dropIndex;
      // if field is being dragged below its current position then it has to be exculuded while calculating drop index
      adjustedDropIndex = sourceIndex < adjustedDropIndex ? adjustedDropIndex - 1 : adjustedDropIndex;
    }

    if (sourceIndex === adjustedDropIndex) {
      this.resetDropIndex();
      return;
    }

    return EncodingHelpers.updateFieldEncoding(encoding, {
      id: field.alias,
      at: fieldArray[adjustedDropIndex]?.id,
    });
  }

  addTrendField(view: FlamingoTypes.ModeView, encoding: Encoding, field: CompiledFormula, fieldId?: string) {
    if (this.channel === 'detail') {
      return addTrendToEncoding(view, encoding, field, this.channel, fieldId);
    }

    return EncodingHelpers.loadEncoding(encoding);
  }

  addField(
    view: FlamingoTypes.ModeView,
    encoding: Encoding,
    field: CompiledFormula,
    fieldId?: string,
    zoneType?: ZoneType
  ) {
    if (this.getExistingField != null) {
      const encField = this.getExistingField(view, encoding, field);
      if (encField != null && this.canDropTest != null) {
        this.alertService.error(`
              ${this.name} already uses ${field.displayName}.
              To use fields more than once they must have unique aggregates.
            `);
        this.chartService.endDrag();
        return;
      }
    }

    if (!field.isField) {
      return addOrReplaceField(EncodingHelpers.loadEncoding(encoding), field, this.channel, fieldId, zoneType);
    } else {
      return updateOrMoveField(EncodingHelpers.loadEncoding(encoding), field, this.channel, fieldId, zoneType);
    }
  }

  fieldDropped(
    $event: DragEvent,
    view: FlamingoTypes.ModeView | null,
    encoding: Encoding,
    fieldId?: string,
    zoneType?: ZoneType
  ) {
    this.hoverZone = null;
    this.dragCounter = 0;
    const dragInfo = this.chartService.dragField;

    let newEncoding: EncodingResult;

    this.analyticsService.track(AnalyticsEvent.Name.DragEnd, {
      location: AnalyticsEvent.SubContext.Chart,
      action: 'Field dropped',
      resource: AnalyticsEvent.Context.Visualization,
      chartType: this.chartType,
      channel: this.channel,
    });

    // Unclear what order of events would result in dragInfo.field being null, but it seems to be possible
    if (dragInfo != null && dragInfo.field != null) {
      const field = dragInfo.field;

      if (field.isField && field.alias === fieldId) {
        // if dropping the field on top of itself, just ignore
        $event.stopPropagation();
        this.resetDropIndex();
        return;
      }

      if (dragInfo.source !== this.channel) {
        // Hacky way to check if we should do a table field add
        if (view == null && field.isField === false) {
          // We currently don't want to allow users to add
          // Analytic or Aggregate calculated fields
          if (field.isNonScalarCalc) {
            $event.stopPropagation();

            const exprStage = field.isAnalyticCalc ? 'Analytic' : 'Aggregate';
            this.alertService.error(
              `${exprStage} values are currently unsupported ` +
                `within table columns. Try using a different chart type.`
            );
            this.resetDropIndex();
            return;
          }

          /**
           * If field is not dropped over a current field but there is a dropIndex
           * that means field was dropped in between two fields
           * Then dropIndex can be used to find relative field id for that position
           */
          const fieldArray = encoding.marks.properties[this.channel].values;
          if ((!fieldId || this.dropAreaSelector) && this.dropIndex !== -1) {
            fieldId = this.dropIndex < fieldArray.length ? fieldArray[this.dropIndex].id : null;
          }

          newEncoding = this.addTableField(encoding, field, fieldId);
        } else {
          if (this.channel === 'detail') {
            newEncoding = this.addTrendField(view, encoding, dragInfo.field, fieldId);
          } else {
            newEncoding = this.addField(view, encoding, dragInfo.field, fieldId, zoneType);
          }
        }
      } else if (this.dropIndex !== -1 && field.isField) {
        newEncoding = this.reorderField(encoding, field);
      } else if (!this.dropAreaSelector && field.isField && fieldId !== null) {
        newEncoding = this.reorderField(encoding, field, fieldId);
      }
    }

    this.resetDropIndex();
    if (newEncoding != null) {
      this.chartService.endDrag();
      this.chartService.dispatchEncodingUpdate(newEncoding);
      $event.stopPropagation();
    }
  }

  dragEnter($event: DragEvent, $fieldId?: string) {
    $event.stopPropagation();
    const dragInfo = this.chartService.dragField;
    this.dragCounter = incDragCounter(this.hoverZone, $fieldId || this.channel, this.dragCounter);
    this.dragEventStream.next($event);
    if (dragInfo == null) {
      $event.preventDefault();
      return;
    }

    const field = dragInfo.field;
    if (
      ($fieldId == null && dragInfo.source === this.channel) ||
      !dragInfo.valid ||
      ($fieldId != null && field.isField && field.alias === $fieldId)
    ) {
      $event.preventDefault();
      return;
    }

    // Prevent dropping into a channel that cannot accept more values
    if (this.channel !== dragInfo.source && this.canDropTest != null && !this.canDropTest(this.channel, field)) {
      $event.preventDefault();
      return;
    }

    this.hoverZone = $fieldId || this.channel;
    setDragIcon($event, dragInfo.source, true);
  }

  dragLeave($event: DragEvent, fieldId?: string) {
    $event.stopPropagation();
    this.dragCounter = decDragCounter(this.hoverZone, fieldId || this.channel, this.dragCounter);
    if (this.dragCounter <= 0) {
      this.resetDropIndex();
      this.hoverZone = null;
    }
  }

  dragOver($event: DragEvent) {
    $event.stopPropagation();
    this.dragEventStream.next($event);

    if (this.hoverZone != null) {
      const dragInfo = this.chartService.dragField;
      setDragIcon($event, dragInfo.source, true);
    } else {
      setDragIcon($event, null, false);
    }
  }

  dragStart($event: any, $field: CompiledFormula, $valid: boolean) {
    this.chartService.startDrag({
      field: $event.$event ? $event.$field : $field,
      source: this.channel,
      valid: $event.$event ? $event.$valid : $valid,
    });

    this.analyticsService.track(AnalyticsEvent.Name.DragStart, {
      location: AnalyticsEvent.SubContext.Chart,
      action: 'Field drag started',
      resource: AnalyticsEvent.Context.Visualization,
      chartType: this.chartType,
      channel: this.channel,
    });
  }

  // Note: `parent` should match the `id` attribute of the containing ancestor element within which if
  // the pill gets dropped, it shouldn't be removed (e.g. a pill dragged from the X-Axis shelf dropped
  // within the same shelf, or a pill dragged from the Filters card dropped within the same card, should
  // not be removed)
  dragEnd($event: any, parent: string, $field?: EncField): void {
    const event = $event.$event ? $event.$event : $event;
    const field = $event.$field ? $event.$field : $field;
    if (
      this.chartService.dragField &&
      !$(document.elementFromPoint(event.clientX, event.clientY)).closest(`#${parent}`).length
    ) {
      this.onRemoveField(field);
    }
    this.resetDropIndex();
    this.chartService.endDrag();
  }

  private subscribeDropIndexHandler() {
    this.dragEventStream$
      .pipe(
        takeUntil(this.destroyed$),
        throttle(() => interval(100))
      )
      .subscribe(($event: DragEvent) => {
        const dropZoneElement = document.querySelector(this.dropAreaSelector);
        const directChild = this.getDirectChildElement(dropZoneElement, $event.target as Element);
        const index = directChild?.getAttribute('data-index');
        if (index) {
          this.dropIndex = this.isDropIndexBeforeElement($event, directChild)
            ? parseFloat(index)
            : parseFloat(index) + 1;
          this.dropIndexStream.next(this.dropIndex);
        }
      });
  }

  private getDirectChildElement(parentElement: Element, childElement: Element): Element | null {
    let directChild: Node = childElement;

    while (directChild.parentNode !== parentElement) {
      // reached root node without finding given parent
      if (!directChild.parentNode || !parentElement) {
        return null;
      }

      directChild = directChild.parentNode;
    }

    return directChild as Element;
  }

  private isDropIndexBeforeElement(event: DragEvent, element: Element) {
    const bounds = element.getBoundingClientRect();

    // If the pointer is in the upper half of the list item element,
    // we position the placeholder before the list item, otherwise after it.
    return event.clientY < bounds.top + bounds.height / 2;
  }

  private resetDropIndex() {
    this.dropIndex = -1;
    this.dropIndexStream.next(-1);
  }
}

export class TableDropZone extends DropZone {
  dragOver($event: DragEvent) {
    $event.stopPropagation();
    this.dragEventStream.next($event);
    const dragInfo = this.chartService.dragField;
    setDragIcon($event, dragInfo.source, true);
  }
}

export function setDragIcon(ev: DragEvent, source: EncChannel | 'field-list', droppable: boolean) {
  if (!droppable) {
    if (ev.dataTransfer != null) {
      ev.dataTransfer.dropEffect = 'none';
    }

    return;
  }

  if (source !== 'field-list') {
    if (ev.dataTransfer != null) {
      ev.dataTransfer.dropEffect = 'move';
    }
  } else {
    if (ev.dataTransfer != null) {
      ev.dataTransfer.dropEffect = 'copy';
    }
  }

  if (droppable) {
    ev.preventDefault();
    ev.stopPropagation();
  }
}

export function incDragCounter(currentZone: EncChannel | string, sourceZone: EncChannel | string, current: number) {
  if (currentZone !== sourceZone) {
    current = 1;
  } else {
    current++;
  }

  return current;
}

export function decDragCounter(currentZone: EncChannel | string, sourceZone: EncChannel | string, current: number) {
  if (currentZone) {
    if (currentZone !== sourceZone) {
      current = 1;
    } else {
      current--;
    }
  } else if (!currentZone && sourceZone === 'text') {
    current--;
  }

  if (current <= 0) {
    current = 0;
  }

  return current;
}
